import DownloadIcon from '@mui/icons-material/Download';
import { IconButton, Stack, Tooltip } from '@mui/material';
import { GetDetailRowDataParams, ICellRendererParams } from 'ag-grid-community';
import { useCallback, useMemo } from 'react';
import { AgTable } from '../../../../components/AgTable/AgTable';
import {
  IStatusRendererParams,
  StatusRenderer,
} from '../../../../components/AgTable/cell-renderers/StatusRenderer';
import { FadeInGridWrapper } from '../../../../components/grid-renderers/FadeInGridWrapper';
import {
  TearSheetsRecord,
  tearSheetsRecordSchema,
  TearSheetsUpload,
  tearSheetsUploadSchema,
} from '../../../../schemas/BulkTearsheetGeneration.schema';
import { getTearsheetsByUploadId } from '../../../../services/queries/MaggieTearsheetQueries';
import { useSchemaToGrid } from '../../../../util/schema-utils';
import { withOverrides } from '../../../../util/ag-grid-utils';

interface TearsheetGenerationGridProps {
  data: TearSheetsUpload[];
}

export function TearsheetGenerationGrid({ data }: TearsheetGenerationGridProps) {
  const bulkGenerateColDefs = useTearsheetGenerationColDefs();
  const detailColumnDefs = useDetailColumnDefs();

  if (bulkGenerateColDefs.length > 0) {
    bulkGenerateColDefs[0].cellRenderer = 'agGroupCellRenderer';
    const statusCol = bulkGenerateColDefs.find((c) => c.field === 'status');

    if (statusCol) {
      statusCol.cellRenderer = (params: ICellRendererParams<TearSheetsUpload>) => {
        const status = (params.value as string)?.toLowerCase() as IStatusRendererParams['status'];

        return <StatusRenderer status={status} value={status} />;
      };
    }
  }

  const getDetailRowData = useCallback(async (params: GetDetailRowDataParams<TearSheetsUpload>) => {
    const uploadId = params.data.id;
    try {
      const detailData = await getTearsheetsByUploadId(uploadId);
      params.successCallback(detailData);
    } catch (error) {
      params.successCallback([]);
    }
  }, []);

  const detailCellRendererParams = useMemo(
    () => ({
      detailGridOptions: {
        columnDefs: detailColumnDefs,
        defaultColDef: detailDefaultColDef,
      },
      getDetailRowData,
    }),
    [detailColumnDefs, getDetailRowData]
  );

  const gridOptions = useMemo(
    () => ({
      masterDetail: true,
      detailRowAutoHeight: true,
      embedFullWidthRows: true,
      detailCellRendererParams,
    }),
    [detailCellRendererParams]
  );

  return (
    <FadeInGridWrapper>
      <Stack sx={{ height: 'calc(100% - 1rem)', mt: '1rem' }}>
        <AgTable
          columnDefs={bulkGenerateColDefs}
          rowData={data}
          defaultColDef={defaultColDef}
          rowGroupPanelShow='never'
          gridOptions={gridOptions}
        />
      </Stack>
    </FadeInGridWrapper>
  );
}

function useTearsheetGenerationColDefs() {
  const schemaToGrid = useSchemaToGrid();
  return useMemo(() => {
    const schema = tearSheetsUploadSchema().pick(['createdAt', 'createdBy', 'status', 'note', 'id']);
    return withOverrides(schemaToGrid(schema), {
      id: {
        filter: false,
        suppressHeaderMenuButton: true,
        maxWidth: 80,
      },
    });
  }, [schemaToGrid]);
}

function TearsheetStatusRenderer(props: ICellRendererParams<TearSheetsRecord>) {
  const status = (props.value as string)?.toLowerCase() as IStatusRendererParams['status'];
  const errorMessage = props.data?.errorMessage;

  return <StatusRenderer {...props} status={status} value={errorMessage ?? 'Done'} />;
}

function TearsheetUrlDownloadRenderer(props: ICellRendererParams<TearSheetsRecord>) {
  const url = props.value;

  if (!url) {
    return null;
  }

  const handleDownload = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <Tooltip title={'download'}>
      <IconButton size='small' color='primary' onClick={handleDownload}>
        <DownloadIcon />
      </IconButton>
    </Tooltip>
  );
}

function useDetailColumnDefs() {
  const schemaToGrid = useSchemaToGrid();
  return useMemo(() => {
    const schema = tearSheetsRecordSchema().pick(['companyId', 'fundId', 'asOfDate', 'status', 'url']);
    const colDefs = schemaToGrid(schema);

    // Add custom cell renderer for status column to show error messages
    const statusColIndex = colDefs.findIndex((col) => col.field === 'status');
    if (statusColIndex !== -1) {
      colDefs[statusColIndex] = {
        ...colDefs[statusColIndex],
        cellRenderer: TearsheetStatusRenderer,
      };
    }

    // Add custom cell renderer for URL column
    const urlColIndex = colDefs.findIndex((col) => col.field === 'url');
    if (urlColIndex !== -1) {
      colDefs[urlColIndex] = {
        ...colDefs[urlColIndex],
        cellRenderer: TearsheetUrlDownloadRenderer,
      };
    }

    return colDefs;
  }, [schemaToGrid]);
}

const defaultColDef = {
  flex: 1,
  sortable: true,
  filter: true,
  resizable: true,
};

const detailDefaultColDef = {
  flex: 1,
  sortable: true,
  filter: true,
  resizable: true,
};
